import React, { useEffect, useState } from 'react';
import {
  EInvoiceStatus,
  IInvoiceData,
  InvoicesState
} from '../../../../../store/ducks/invoices/types';
import { Container, FilterContent, FilterInputContainer } from './styles';
import moment from 'moment';
import { formatCurrency } from '../../../../../utils/formatNumbers';
import { useSelector, useDispatch } from 'react-redux';
import { ApplicationState } from '../../../../../store';
import { ECustomerTypeView } from '../../../../../store/ducks/user/types';
import { uppercaseOnlyFirstLetter } from '../../../../../utils/formatStrings';
import { Button, Select, SearchField, Title } from '@gerdau/hefesto';
import { TSelectOptionProps } from '@gerdau/hefesto/Select/Select.types';
import { sendTagging } from '../../../../../store/ducks/tagging/actions';
import { ITaggingParams } from '../../../../../store/ducks/tagging/types';
import { ICustomer } from '../../../../../store/ducks/customers/types';

interface IDuplicatesFilterProps {
  invoicesState: InvoicesState;
  filteredInvoices: IInvoiceData[];
  setFilteredInvoices(filteredInvoices: IInvoiceData[]): void;

  searchInput: string;
  setSearchInput(value: string): void;
  onCleanFilters(): void;
}

interface IFilterOptions {
  value: string;
  label: string;
}

const DuplicatesFilter: React.FC<IDuplicatesFilterProps> = ({
  invoicesState,
  setFilteredInvoices,
  searchInput,
  setSearchInput,
  onCleanFilters
}) => {
  const dispatch = useDispatch();
  const customerViewType = useSelector(
    (state: ApplicationState) => state.user.searchCustomerType
  );
  const invoices = useSelector((state: ApplicationState) => state.invoices);
  const [selectedStatus, setSelectedStatus] = useState<EInvoiceStatus | string>(
    EInvoiceStatus.ALL
  );
  const _ALL_OPTION_VALUE = 'all';
  const [customerId, setCustomerId] = useState<string | undefined>(
    _ALL_OPTION_VALUE
  );

  const prepareAndSendTagging = () => {
    const taggingParams: ITaggingParams = {
      screenName: 'LG:Financas:MinhasFinancas:Duplicatas',
      event: 'click',
      itemName: 'button:limpar_filtros',
      itemDescription: 'financas_minhas_financas_duplicatas_limparFiltros'
    };
    dispatch(sendTagging(taggingParams));
  };

  const cleanFilters = () => {
    prepareAndSendTagging();
    cleanFilterInputs();
    onCleanFilters();
  };

  const cleanFilterInputs = () => {
    setSelectedStatus('');
    setCustomerId(_ALL_OPTION_VALUE);
    setSearchInput('');
  };

  useEffect(() => {
    if (invoicesState.loading) {
      cleanFilterInputs();
    }
  });

  const onFilter = (
    newStatusValue: EInvoiceStatus,
    newCustomerId: string | undefined,
    newSearchInputValue: string
  ) => {
    const handleFilterByStatus = (
      status: EInvoiceStatus,
      invoices: IInvoiceData[]
    ) => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      if ((newStatusValue as any) === '0') {
        return invoices;
      }
      return invoices.filter((invoice) => {
        return (
          invoice.status ===
          statusOptions.find((statusOp) => statusOp.value === status)?.label
        );
      });
    };

    const handleFilterByReceiver = (
      newReceiverId: string,
      invoices: IInvoiceData[]
    ) => {
      return invoices.filter((invoice) => {
        return (
          newReceiverId === _ALL_OPTION_VALUE ||
          invoice.receiver.code === newReceiverId
        );
      });
    };

    const handleFilterBySearchInput = (
      searchInputValue: string,
      invoices: IInvoiceData[]
    ) => {
      return invoices.filter((invoice) => {
        return (
          invoice.invoiceNumber.includes(searchInputValue) ||
          invoice.purchaseOrder.toUpperCase().includes(searchInputValue) ||
          invoice.documentType?.toUpperCase().includes(searchInputValue) ||
          moment(invoice.invoiceDate, 'YYYY-MM-DD')
            .format('DD/MM/YYYY')
            .includes(searchInputValue) ||
          moment(invoice.dueDate, 'YYYY-MM-DD')
            .format('DD/MM/YYYY')
            .includes(searchInputValue) ||
          invoice.status.toUpperCase().includes(searchInputValue) ||
          formatCurrency(invoice.openInvoiceAmount).includes(searchInputValue)
        );
      });
    };
    let newfilteredInvoices = invoicesState.invoices || [];

    setSearchInput(newSearchInputValue);
    newfilteredInvoices = handleFilterBySearchInput(
      newSearchInputValue.toUpperCase(),
      newfilteredInvoices
    );

    setSelectedStatus(newStatusValue);

    if (
      !isNaN(parseInt(newStatusValue)) &&
      statusOptions.find((statusOp) => statusOp.value === status)?.label !==
        EInvoiceStatus.ALL
    ) {
      newfilteredInvoices = handleFilterByStatus(
        newStatusValue,
        newfilteredInvoices
      );
    }

    if (newCustomerId !== undefined && newCustomerId !== '') {
      setCustomerId(newCustomerId);
      newfilteredInvoices = handleFilterByReceiver(
        newCustomerId,
        newfilteredInvoices
      );
    }

    setFilteredInvoices(newfilteredInvoices);
  };

  const options = (list?: ICustomer[]) => {
    if (!list) return [];

    const formattedObj: TSelectOptionProps[] = [
      { label: 'Todos', value: 'all' }
    ];

    Object.values(list).map((customer) =>
      formattedObj.push({
        value: customer.customerId,
        label: `${uppercaseOnlyFirstLetter(customer.name)}`,
        descriptions: [
          `BP: ${customer.customerId}`,
          `CNPJ: ${customer.taxNumber ? customer.taxNumber : ' - '}`,
          `${uppercaseOnlyFirstLetter(customer.locality.address)} | 
                ${uppercaseOnlyFirstLetter(customer.locality.city)} - 
                ${customer.locality.state}`
        ]
      })
    );

    return formattedObj;
  };

  const getStatusOptionsFromInvoices = () => {
    let filterOptions: IFilterOptions[] = [];
    let filterClosed = false;
    const invoicesStatus = {
      Paga: { value: '1', label: EInvoiceStatus.CLOSED },
      'A vencer': { value: '2', label: EInvoiceStatus.WILL_DUE },
      Vencida: { value: '3', label: EInvoiceStatus.DUE },
      Todos: { value: '0', label: EInvoiceStatus.ALL }
    };

    const allOptions = invoices.invoices?.map((invoice) => {
      if (invoice.status === EInvoiceStatus.CLOSED && filterClosed === false) {
        filterClosed = true;
      }
      return invoicesStatus[invoice.status] ?? { value: '0', label: 'Todos' };
    });

    filterOptions = filterOptions.concat(allOptions ?? []);

    filterOptions = filterOptions.filter(
      (filterOption, index, self) =>
        self.findIndex(
          (selfOption) => selfOption.label === filterOption.label
        ) === index
    );

    if (filterOptions.length > 1 && !filterClosed) {
      filterOptions.push({ value: '0', label: 'Todos' });
    }

    return filterOptions;
  };

  const statusOptions: TSelectOptionProps[] = getStatusOptionsFromInvoices();

  const customer =
    customerViewType === ECustomerTypeView.EMITTER
      ? options(invoicesState.receivers)
      : options(invoicesState.emitters);

  return (
    <Container>
      <FilterContent>
        <Title level={3}>Filtros</Title>
        <div className="inputs-container">
          <div>
            <FilterInputContainer>
              <Select
                data-testid="select-recebedor-emissor-pedido"
                label={`${uppercaseOnlyFirstLetter(
                  customerViewType === ECustomerTypeView.EMITTER
                    ? 'recebedor'
                    : 'emissor'
                )} do pedido`}
                disabled={invoicesState.loading}
                placeholder={`- Selecione -`}
                name="CustomerSelect"
                value={customerId}
                options={customer}
                onSelect={(value) =>
                  onFilter(
                    selectedStatus as EInvoiceStatus,
                    value.toString(),
                    searchInput
                  )
                }
              ></Select>
            </FilterInputContainer>

            <FilterInputContainer>
              <Select
                data-testid="select-status"
                label="Status"
                disabled={invoicesState.loading}
                name="StatusSelect"
                value={selectedStatus}
                options={statusOptions}
                placeholder={`- Selecione -`}
                onSelect={(value) =>
                  onFilter(value as EInvoiceStatus, customerId, searchInput)
                }
              ></Select>
            </FilterInputContainer>
          </div>

          <div>
            <FilterInputContainer>
              <SearchField
                data-testid="hefesto-search-field"
                label="Pesquisar"
                name="search"
                inputValue={searchInput}
                disabled={invoicesState.loading}
                onChange={(e) => {
                  onFilter(
                    selectedStatus as EInvoiceStatus,
                    customerId,
                    e.target.value
                  );
                }}
                helperText="Pesquise pelo nº da nota fiscal, nº do pedido ou regional"
                placeholder="Pesquise uma fatura."
              />
            </FilterInputContainer>
            <Button
              type="button"
              name="btnCleanFilters"
              onClick={cleanFilters}
              variant="secondary"
              disabled={invoicesState.loading}
              loading={invoicesState.loading}
            >
              Limpar filtros
            </Button>
          </div>
        </div>
      </FilterContent>
    </Container>
  );
};

export default DuplicatesFilter;
